import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ip_back } from "../config";
import { useBrand } from "../context/BrandContext";
import { getPlayerProfileStats, updatePlayerGear } from "./funtionsBD/Mainservice";
import {
  isPlayerAccount,
  readActiveHomeRole,
  userHasPlayerRole,
} from "../utils/homeRole";
import { ProfileSmartTab } from "./ProfileSmartTab";
import CoachProfilePanel from "./CoachProfilePanel";
import { fetchSmartProfile } from "./funtionsBD/digipingSmartService";
import { isPingfloTourRunning } from "../tour/tourTarget";
import { PINGFLO_TOUR_CLEARED_EVENT, UI_TOUR_SETTLED_EVENT, OPEN_PROFILE_SMART_EVENT } from "../tour/tourEvents";
import { openDigitalTwin } from "./HomeDailyFocus";
import { startPingfloTour } from "../tour";
import { currentAppLanguage } from "../utils/appLanguages";
import {
  isScforhSeedComplete,
} from "../constants/scforhClubHealth";
import { resolveWhoBandFromBirthDate } from "../constants/whoPhysicalActivityByAge";
import ScforhWhoCapsule from "./ScforhWhoCapsule";
import ScforhDimensionStatus from "./ScforhDimensionStatus";
import { SelfConsentCard } from "./consent/SelfConsentCard";
import ParentProfilePanel from "./ParentProfilePanel";
import AdminProfilePanel from "./AdminProfilePanel";
import ParentGuardianAvatar from "./ParentGuardianAvatar";
import ClubLogoMark from "./ClubLogoMark";
import {
  formatClassementWeekDelta,
  formatPointsWeekDelta,
  formatPlaceWeekDelta,
  formatPointsValue,
  formatRankingPlace,
} from "../utils/flttRankingDisplay";
import { pickNationalRanking } from "../utils/nationalRanking";
import { FlttRankingSlip } from "./FlttRankingSlip";
import { FlttFormDialog } from "./newcomer/FlttFormDialog";
import {
  federationClubFromUser,
  isLuxembourgFederationClub,
} from "../utils/clubCountryLanguage";
import "./Userinfo.css";

const toAbsoluteImageUrl = (value) => {
  if (!value || typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/^(https?:)?\/\//i.test(trimmed) || trimmed.startsWith("data:")) return trimmed;
  if (trimmed.startsWith("/")) return `${ip_back.replace(/\/$/, "")}${trimmed}`;
  return `${ip_back}${trimmed.replace(/^\/+/, "")}`;
};

const resolveInitialTab = (location) => {
  const tab = location?.state?.tab ?? location?.state?.activeTab;
  if (tab === "smart" || tab === "SMART" || tab === 1 || tab === "1") return 1;
  if (tab === "gear" || tab === 2 || tab === "2") return 2;
  if (tab === "stats" || tab === 3 || tab === "3") return 3;
  const params = new URLSearchParams(location?.search || "");
  const q = (params.get("tab") || "").toLowerCase();
  if (q === "smart") return 1;
  if (q === "gear") return 2;
  if (q === "stats") return 3;
  return 0;
};

export const UserInfo = (props) => {
  const { user } = props;
  const { t, i18n } = useTranslation();
  const { brand } = useBrand();
  const location = useLocation();
  const currentLang = (i18n.language || "en").split("-")[0];
  const [clubLogoFailed, setClubLogoFailed] = useState(false);
  const [homeRole, setHomeRole] = useState(() => readActiveHomeRole(user));
  const [parentRelationship, setParentRelationship] = useState(
    () =>
      user?.guardian_relationship ||
      user?.relationship ||
      user?.parent_relationship ||
      null
  );
  const isPlayer = isPlayerAccount(user, { homeRole });
  const isParent = homeRole === "parent";
  const isCoachView = homeRole === "coach" || homeRole === "monitor";
  const isAdminStaff =
    (homeRole === "admin" || homeRole === "committee" || homeRole === "moderator") &&
    !isPlayer &&
    !isParent;
  // Dual-role admin/coach who also plays must still grant video/HR for themselves.
  const showStaffSelfConsent =
    !isParent &&
    !isPlayer &&
    (isAdminStaff || isCoachView || userHasPlayerRole(user));

  const relationshipRaw =
    parentRelationship ||
    user?.guardian_relationship ||
    user?.relationship ||
    user?.parent_relationship ||
    "parent";
  const relationshipLabel = (() => {
    const r = String(relationshipRaw || "").toLowerCase();
    if (r === "father") return t("parentWizardRelFather") || "Father";
    if (r === "mother") return t("parentWizardRelMother") || "Mother";
    if (r === "tutor" || r.includes("guardian"))
      return t("parentWizardRelTutor") || "Tutor / Guardian";
    return t("parentRoleLabel") || "Parent / Guardian";
  })();
  
  // Interactive Tab State
  const [activeTab, setActiveTab] = useState(() => resolveInitialTab(location));
  const [flttOpen, setFlttOpen] = useState(false);
  const actingClub = federationClubFromUser(user, brand);
  const showFlttForm = isPlayer && isLuxembourgFederationClub(actingClub);

  const openSmartSection = useCallback(() => {
    setActiveTab(1);
  }, []);

  useEffect(() => {
    const hasExplicitTab =
      location?.state?.tab != null ||
      location?.state?.activeTab != null ||
      Boolean(new URLSearchParams(location?.search || "").get("tab"));
    if (!hasExplicitTab) return;
    setActiveTab(resolveInitialTab(location));
  }, [location?.state?.tab, location?.state?.activeTab, location?.search]);

  useEffect(() => {
    window.addEventListener(OPEN_PROFILE_SMART_EVENT, openSmartSection);
    return () => window.removeEventListener(OPEN_PROFILE_SMART_EVENT, openSmartSection);
  }, [openSmartSection]);

  useEffect(() => {
    const sync = () => setHomeRole(readActiveHomeRole(user));
    sync();
    window.addEventListener("smarttt-home-role-changed", sync);
    return () => window.removeEventListener("smarttt-home-role-changed", sync);
  }, [user]);

  const [blade, setBlade] = useState("");
  const [forehand, setForehand] = useState("");
  const [backhand, setBackhand] = useState("");
  const [gearWeight, setGearWeight] = useState("");
  const [gearSaving, setGearSaving] = useState(false);

  const [statsLoading, setStatsLoading] = useState(false);
  const [profileStats, setProfileStats] = useState(null);
  const [clubScforhSmart, setClubScforhSmart] = useState(null);

  // Inline editing state: null, 'blade', 'forehand', 'backhand'
  const [editingSlot, setEditingSlot] = useState(null);
  const [tempGearName, setTempGearName] = useState("");
  const [showFeedback, setShowFeedback] = useState(false);

  const syncGearFromUser = useCallback((source) => {
    if (!source) return;
    setBlade(source.player_gear_blade || "");
    setForehand(source.player_gear_forehand || "");
    setBackhand(source.player_gear_backhand || "");
    setGearWeight(source.player_gear_weight || "");
  }, []);

  useEffect(() => {
    syncGearFromUser(user);
  }, [
    user?.player_gear_blade,
    user?.player_gear_forehand,
    user?.player_gear_backhand,
    user?.player_gear_weight,
    syncGearFromUser,
  ]);

  useEffect(() => {
    if (!isPlayer) {
      setStatsLoading(false);
      setProfileStats(null);
      return undefined;
    }
    let cancelled = false;
    const loadStats = async () => {
      setStatsLoading(true);
      try {
        const response = await getPlayerProfileStats();
        if (!cancelled && response?.data?.status === "success") {
          setProfileStats(response.data.data);
        }
      } catch (error) {
        console.error("Failed to load profile stats:", error);
        if (!cancelled) setProfileStats(null);
      } finally {
        if (!cancelled) setStatsLoading(false);
      }
    };
    loadStats();
    return () => {
      cancelled = true;
    };
  }, [user?.user_id, isPlayer]);

  useEffect(() => {
    if (isPlayer || isParent || isCoachView) {
      setClubScforhSmart(null);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      if (isPingfloTourRunning()) return;
      try {
        const profile = await fetchSmartProfile(null, currentLang);
        if (!cancelled) setClubScforhSmart(profile);
      } catch {
        if (!cancelled) setClubScforhSmart(null);
      }
    };
    load();
    window.addEventListener(PINGFLO_TOUR_CLEARED_EVENT, load);
    window.addEventListener(UI_TOUR_SETTLED_EVENT, load);
    return () => {
      cancelled = true;
      window.removeEventListener(PINGFLO_TOUR_CLEARED_EVENT, load);
      window.removeEventListener(UI_TOUR_SETTLED_EVENT, load);
    };
  }, [user?.user_id, isPlayer, isParent, isCoachView, currentLang]);

  useEffect(() => {
    if (!isPlayer && activeTab !== 0) setActiveTab(0);
  }, [isPlayer, activeTab]);

  useEffect(() => {
    setClubLogoFailed(false);
  }, [
    brand?.logo_url,
    user?.club?.club_image,
    user?.club_details?.club_image,
    user?.club_image,
  ]);

  // Handle equipment editing inline
  const startEditingGear = (slot, currentValue) => {
    setEditingSlot(slot);
    setTempGearName(currentValue);
  };

  const cancelEditingGear = () => {
    setEditingSlot(null);
    setTempGearName("");
  };

  const saveEditingGear = async (slot) => {
    if (!tempGearName.trim() || gearSaving) return;

    const payload = {};
    if (slot === "blade") payload.player_gear_blade = tempGearName.trim();
    else if (slot === "forehand") payload.player_gear_forehand = tempGearName.trim();
    else if (slot === "backhand") payload.player_gear_backhand = tempGearName.trim();

    setGearSaving(true);
    try {
      const response = await updatePlayerGear(payload);
      const saved = response?.data?.data || payload;

      if (slot === "blade") setBlade(saved.player_gear_blade || tempGearName.trim());
      else if (slot === "forehand") setForehand(saved.player_gear_forehand || tempGearName.trim());
      else if (slot === "backhand") setBackhand(saved.player_gear_backhand || tempGearName.trim());

      try {
        const stored = JSON.parse(localStorage.getItem("user") || "{}");
        localStorage.setItem(
          "user",
          JSON.stringify({
            ...stored,
            player_gear_blade: saved.player_gear_blade ?? stored.player_gear_blade,
            player_gear_forehand: saved.player_gear_forehand ?? stored.player_gear_forehand,
            player_gear_backhand: saved.player_gear_backhand ?? stored.player_gear_backhand,
          })
        );
      } catch {
        /* ignore */
      }

      setEditingSlot(null);
      setTempGearName("");
      setShowFeedback(true);
      setTimeout(() => setShowFeedback(false), 2500);
    } catch (error) {
      console.error("Failed to save player gear:", error);
    } finally {
      setGearSaving(false);
    }
  };

  const displayGearValue = (value) => (value && String(value).trim() ? value : t("notSet"));

  // Get user type display name - identical base-role filtering logic preserved
  const getUserTypeDisplay = (typeId) => {
    if (user.all_role_names && Array.isArray(user.all_role_names) && user.all_role_names.length > 0) {
      const roleDisplayNames = user.all_role_names
        .map(roleName => {
          const lowerName = String(roleName).toLowerCase().trim();
          let extractedRole = null;
          
          if (lowerName.includes('super admin') || lowerName.includes('superadmin')) {
            extractedRole = t('superAdmin') || 'SuperAdmin';
          } else if (lowerName.includes('coach')) {
            extractedRole = 'Coach';
          } else if (lowerName.includes('parent')) {
            extractedRole = t('parentRoleLabel') || 'Parent';
          } else if (lowerName.includes('player')) {
            extractedRole = 'Player';
          } else if (lowerName.includes('admin')) {
            extractedRole = 'Admin';
          }
          return extractedRole;
        })
        .filter(role => role !== null && role !== undefined && role !== '')
        .filter((role, index, self) => self.indexOf(role) === index);
      
      return roleDisplayNames.join(' + ');
    }
    
    const hasPlayerData = user.player_grip || user.player_hand || user.player_level || user.style;
    const isAdmin = typeId === 1;
    
    if (isAdmin && hasPlayerData) {
      return `${t('admin')} + ${t('player')}`;
    }
    
    switch (typeId) {
      case 1: return 'SuperAdmin';
      case 2: return t('admin');
      case 3: return t('coach');
      case 4: return t('player');
      case 5: return t('parentRoleLabel') || 'Parent';
      default: return t('user');
    }
  };

  const getPlayerLevelDisplay = () => {
    const hasValidLevel = user.player_level && 
                          typeof user.player_level === 'object' && 
                          (user.player_level.level_name || user.player_level.level_description);
    
    if (hasValidLevel || (typeof user.player_level === 'string' && user.player_level)) {
      if (typeof user.player_level === 'string') {
        return user.player_level;
      }
      
      if (typeof user.player_level === 'object' && user.player_level !== null) {
        const levelName = user.player_level?.level_name;
        if (levelName && levelName !== '' && levelName !== null && levelName !== undefined) {
          if (user.main_purpose_id && user.main_purpose_id !== 3) {
            switch (levelName) {
              case 'C3': return t('beginner') || 'Beginner';
              case 'B1': return t('intermediate') || 'Intermediate';
              case 'A1': return t('advanced') || 'Advanced';
              default: return levelName;
            }
          }
          return levelName;
        }
        
        if (user.player_level?.level_description) {
          return user.player_level.level_description;
        }
      }
    }
    
    const clubClassement = pickNationalRanking(user, brand)?.classement;
    if (clubClassement) {
      return clubClassement;
    }

    if (user.custom_level) {
      return user.custom_level;
    }

    const exp = user.experienceLevel || user.experience_level;
    if (exp) {
      const fromTranslation = exp.translations?.[0]?.name;
      if (fromTranslation) return fromTranslation;
      if (exp.experience_level_name) return exp.experience_level_name;
      if (exp.name) return exp.name;
    }
    
    return "N/A";
  };

  const getPlayerGrip = () => {
    if (user.player_grip) {
      switch (user.player_grip) {
        case 'shakehand': return t('shakehand') || 'Shakehand';
        case 'penhold': return t('penhold') || 'Penhold';
        default: return user.player_grip;
      }
    }
    return "N/A";
  };

  const getPlayerHand = () => {
    if (user.player_hand) {
      switch (user.player_hand) {
        case 'right': return t('rightHand') || 'Right-Handed';
        case 'left': return t('leftHand') || 'Left-Handed';
        default: return user.player_hand;
      }
    }
    return "N/A";
  };

  const getPlayerStyle = () => {
    if (user.style) {
      if (user.style.translations && user.style.translations[0] && user.style.translations[0].name) {
        return user.style.translations[0].name;
      }
      if (user.style.style_name) {
        return user.style.style_name;
      }
    }
    return "N/A";
  };

  const getProfileRanking = () => {
    const picked = pickNationalRanking(user, brand);
    const slip = profileStats?.officialRanking || null;
    if (!picked && !slip) return null;
    const classement = picked?.classement ?? slip?.classement ?? null;
    const ranking_points =
      picked?.ranking_points ?? slip?.ranking_points ?? null;
    const ranking_place =
      picked?.ranking_place ?? slip?.ranking_place ?? null;
    if (
      classement == null &&
      ranking_points == null &&
      ranking_place == null &&
      !picked?.ranking_system
    ) {
      return null;
    }
    return {
      ranking_system: picked?.ranking_system || null,
      ranking_system_label: picked?.ranking_system_label || null,
      classement,
      ranking_points,
      ranking_place,
      points_week_delta:
        picked?.points_week_delta ?? slip?.points_delta ?? null,
      place_week_delta:
        picked?.place_week_delta ?? slip?.place_delta ?? null,
      classement_from:
        picked?.classement_from ?? slip?.classement_from ?? null,
      classement_delta:
        picked?.classement_delta ?? slip?.classement_delta ?? null,
    };
  };

  const getClubName = () => {
    const candidates = [
      user?.club_details?.club_name,
      user?.club?.club_name,
      user?.club_name,
      brand?.club_name,
      brand?.brand_short_name,
      brand?.brand_name,
    ];
    for (const c of candidates) {
      const name = String(c || "").trim();
      if (!name) continue;
      // Skip generic platform default when a real club wasn't resolved
      if (name === "Smart TT" || name === "SmartTT") continue;
      return name;
    }
    return t("notAvailable") || "N/A";
  };

  const getClubLogo = () => {
    const raw =
      user?.club_details?.club_image ||
      user?.club?.club_image ||
      user?.club_image ||
      brand?.logo_url ||
      null;
    return toAbsoluteImageUrl(raw);
  };

  const blind =
    clubScforhSmart?.pathMeta?.validateExistingClubPlan &&
    !clubScforhSmart?.pathMeta?.clubPlanValidated;
  const scforhSeed = blind
    ? clubScforhSmart?.pathMeta?.incomingScforhSeed ||
      clubScforhSmart?.pathMeta?.scforhSeed ||
      null
    : user?.club_details?.scforh_profile ||
      user?.club?.scforh_profile ||
      clubScforhSmart?.pathMeta?.scforhSeed ||
      null;

  const scforhSeedChips = (() => {
    if (!scforhSeed || typeof scforhSeed !== "object") return [];
    const chips = [];
    if (scforhSeed.sportingAims) {
      chips.push(t(`scforhSportingAims_${scforhSeed.sportingAims}`));
    }
    (scforhSeed.targetGroups || []).forEach((id) => {
      chips.push(t(`scforhTargetGroups_${id}`));
    });
    (scforhSeed.healthDimensions || []).forEach((id) => {
      chips.push(t(`scforhHealthDimensions_${id}`));
    });
    return chips.filter(Boolean);
  })();

  const getObjectiveNameByLanguage = (goal) => {
    if (!goal || typeof goal !== "object") return "";
    if (goal.custom_text && String(goal.custom_text).trim()) {
      return String(goal.custom_text).trim();
    }
    const langMap = {
      en: ["translated_name_en", "attribute_name_en", "name_en"],
      fr: ["translated_name_fr", "attribute_name_fr", "name_fr"],
      de: ["translated_name_de", "attribute_name_de", "name_de"],
      es: ["translated_name_es", "attribute_name_es", "name_es"],
      nl: ["translated_name_nl", "attribute_name_nl", "name_nl"],
      pt: ["translated_name_pt", "attribute_name_pt", "name_pt"],
    };
    const preferredKeys = langMap[currentLang] || langMap.en;
    const fallbackKeys = [
      "translated_name",
      "attribute_name",
      "name",
      "translated_name_en",
      "attribute_name_en",
      "name_en",
    ];

    for (const key of preferredKeys) {
      if (goal[key]) return goal[key];
    }
    for (const key of fallbackKeys) {
      if (goal[key]) return goal[key];
    }
    return "";
  };

  const clubLogoUrl = getClubLogo();

  const weeklyMins = profileStats?.weeklyTrainingMinutes ?? 0;
  const weeklyGoal = profileStats?.weeklyGoalMinutes ?? 150;
  const progressPct = weeklyGoal > 0 ? Math.min(1, weeklyMins / weeklyGoal) : 0;
  const activeStreakDays = profileStats?.activeStreakDays ?? 0;
  const lastCardio = profileStats?.lastSessionCardio || {};
  const ringRadius = 32;
  const ringCircumference = 2 * Math.PI * ringRadius;
  const ringStrokeOffset = ringCircumference - progressPct * ringCircumference;

  const formatHr = (value) =>
    value != null && Number.isFinite(Number(value))
      ? `${Math.round(Number(value))} ${t("bpm")}`
      : t("notAvailable");

  const formatCalories = (value) =>
    value != null && Number.isFinite(Number(value))
      ? `${Math.round(Number(value))} ${t("kcal")}`
      : t("notAvailable");

  return (
    <div className="user-info">
      <div className="ios-profile-card">
        {/* Profile Header */}
        <div className="ios-profile-header">
          <div className="ios-avatar-wrapper">
            {user.user_image ? (
              <img src={user.user_image} alt="" className="ios-avatar" />
            ) : isParent ? (
              <ParentGuardianAvatar
                relationship={relationshipRaw}
                firstName={user.user_first_name || user.user_alias || ""}
                size={72}
                className="ios-avatar parent-guardian-avatar--profile"
              />
            ) : (
              <div className="ios-avatar-placeholder">
                {user.user_alias !== null
                  ? user.user_alias?.slice(0, 1)
                  : user.user_first_name?.slice(0, 1)}
              </div>
            )}
          </div>
          <h2>
            {isParent
              ? [user.user_first_name, user.user_last_name]
                  .filter(Boolean)
                  .join(" ")
                  .trim() ||
                user.user_alias ||
                t("parentRoleLabel") ||
                "Parent"
              : user.user_alias !== null
                ? user.user_alias
                : user.user_first_name}
          </h2>
          {isParent ? (
            <p className="ios-profile-relationship">{relationshipLabel}</p>
          ) : null}
          {getClubName() ? (
            <div className={`ios-club-badge${isParent ? " ios-club-badge--parent" : ""}`}>
              {clubLogoUrl && !clubLogoFailed ? (
                <img
                  className="ios-club-logo"
                  src={clubLogoUrl}
                  alt=""
                  onError={() => setClubLogoFailed(true)}
                />
              ) : (
                <ClubLogoMark
                  size={isParent ? "md" : "sm"}
                  className="ios-club-logo-placeholder"
                  title={getClubName()}
                />
              )}
              <span className="ios-club-name">{getClubName()}</span>
            </div>
          ) : null}
          {brand?.show_ai_coach !== false ? (
            <button
              type="button"
              className="pingflo-tour-replay"
              onClick={() =>
                startPingfloTour({ replay: true, role: readActiveHomeRole(user) })
              }
            >
              {t("tour_replay")}
            </button>
          ) : null}
          {isParent &&
          user.user_alias &&
          user.user_alias !==
            [user.user_first_name, user.user_last_name]
              .filter(Boolean)
              .join(" ")
              .trim() ? (
            <p className="ios-profile-alias">{user.user_alias}</p>
          ) : null}
          <p className="ios-email">{user.user_email}</p>

          {isParent ? (
            <ParentProfilePanel
              user={user}
              scforhSeed={scforhSeed}
              onRelationshipDetected={setParentRelationship}
              flttClub={isLuxembourgFederationClub(actingClub)}
            />
          ) : isCoachView ? (
            <CoachProfilePanel />
          ) : isAdminStaff ? (
            <AdminProfilePanel
              scforhSeed={scforhSeed}
              clubScforhSmart={clubScforhSmart}
            />
          ) : !isPlayer ? (
            <div>
            <div className="ios-objectives-card club-health-card">
              <div className="ios-objectives-header">
                <span>💚</span>
                <h3>{t("clubForHealthTitle")}</h3>
              </div>
              {scforhSeed?.targetGroups?.length || scforhSeed?.sportingAims || scforhSeed?.currentOffer ? (
                <ScforhDimensionStatus seed={scforhSeed} />
              ) : null}
              {isScforhSeedComplete(scforhSeed) && scforhSeedChips.length ? (
                <div className="club-health-chips">
                  {scforhSeedChips.map((label, idx) => (
                    <span key={`${idx}-${label}`} className="club-health-chip">
                      {label}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="club-health-empty">{t("clubForHealthEmpty")}</p>
              )}
              <div className="club-health-letters" aria-label="S M A R T">
                {["S", "M", "A", "R", "T"].map((letter) => {
                  const st = clubScforhSmart?.status?.[letter] || "empty";
                  return (
                    <span
                      key={letter}
                      className={`club-health-letter club-health-letter--${st}`}
                    >
                      {letter}
                    </span>
                  );
                })}
              </div>
              <button
                type="button"
                className="club-health-cta"
                onClick={() =>
                  openDigitalTwin({
                    home_role: "admin",
                    seedMessage: t("digipingClubScforhHomeSeed", {
                      lng: currentAppLanguage(i18n),
                    }),
                    seedVisible: false,
                    profilingIntro: "clubScforh",
                    seedKey: "digipingClubScforhHomeSeed",
                  })
                }
              >
                {t("clubForHealthContinue")}
              </button>
              <details className="parent-profile-scforh-expand">
                <summary>
                  {t("whoCapsuleEyebrow") || "WHO · Sports Club for Health"}
                </summary>
                {scforhSeed?.targetGroups?.length ? (
                  <ScforhWhoCapsule
                    targetGroups={scforhSeed.targetGroups}
                    variant="compact"
                  />
                ) : (
                  <ScforhWhoCapsule
                    bands={["youth", "adults", "seniors"]}
                    variant="compact"
                  />
                )}
              </details>
            </div>
            </div>
          ) : null}

          {showStaffSelfConsent ? (
            <SelfConsentCard userId={user?.user_id} />
          ) : null}

          {/* Weekly activity progress — players only */}
          {isPlayer ? (
          <>
          <div className="ios-gaming-level-container">
            <div className="ios-gaming-level-row">
              <div className="ios-gaming-level-badge">
                LVL {user.player_level?.level_name || getPlayerLevelDisplay() || "—"}
              </div>
              <span className="ios-gaming-xp-text">
                {statsLoading
                  ? t("loading")
                  : t("weeklyTrainingProgress", { current: weeklyMins, goal: weeklyGoal })}
              </span>
            </div>
            <div
              className="ios-gaming-xp-track"
              role="progressbar"
              aria-valuenow={Math.round(progressPct * 100)}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={t("weeklyTraining")}
            >
              <div
                className="ios-gaming-xp-bar"
                style={{ width: `${Math.round(progressPct * 100)}%` }}
              />
            </div>
            <span className="ios-gaming-streak-badge">
              {activeStreakDays > 0
                ? `🔥 ${t("dayStreakCompact", { count: activeStreakDays })}`
                : t("noActiveStreak")}
            </span>
          </div>
          </>
          ) : null}
        </div>

        {isPlayer ? (
        <>
        {/* Brand-colored sliding segmented tabs */}
        <div className="ios-segmented-control" role="tablist">
          <div
            className="ios-segmented-indicator"
            style={{
              width: "calc((100% - 8px) / 4)",
              left: "4px",
              transform: `translateX(calc(${activeTab} * 100%))`,
            }}
          />
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 0}
            className={`ios-segmented-control-btn ${activeTab === 0 ? "active" : ""}`}
            onClick={() => setActiveTab(0)}
          >
            {t("profile")}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 1}
            className={`ios-segmented-control-btn ${activeTab === 1 ? "active" : ""}`}
            onClick={() => setActiveTab(1)}
          >
            {Number(user?.main_purpose_id) === 2 ? t("challengesTab") : t("smartTab")}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 2}
            className={`ios-segmented-control-btn ${activeTab === 2 ? "active" : ""}`}
            onClick={() => setActiveTab(2)}
          >
            {t("stats")}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 3}
            className={`ios-segmented-control-btn ${activeTab === 3 ? "active" : ""}`}
            onClick={() => setActiveTab(3)}
          >
            {t("mygear")}
          </button>
        </div>

        {/* Tab Contents */}
        <div className="ios-tab-content-wrapper">
          {activeTab === 0 && (
            <div className="ios-tab-content">
              {showFlttForm ? (
                <section className="ios-fltt-form">
                  <strong>FLTT affiliation</strong>
                  <p>Generate the form from this profile. Pingflo asks for any detail still missing, then prints it to sign.</p>
                  <button type="button" onClick={() => setFlttOpen(true)}>Generate the form</button>
                  <FlttFormDialog
                    open={flttOpen}
                    userId={user?.user_id}
                    clubId={actingClub?.club_id}
                    onClose={() => setFlttOpen(false)}
                  />
                </section>
              ) : null}
              {/* Attribute 2x2 Grid */}
              <div className="ios-attribute-grid">
                {/* Level */}
                <div className="ios-attribute-card">
                  <div className="ios-attribute-icon-wrapper" style={{ color: "#FFCC00" }}>
                    🏆
                  </div>
                  <div className="ios-attribute-info">
                    <span className="ios-attribute-title">{t("playingLevel")}</span>
                    <span className="ios-attribute-value">{getPlayerLevelDisplay()}</span>
                  </div>
                </div>

                {/* Classement level + rating points */}
                {(() => {
                  const ranking = getProfileRanking();
                  const points = formatPointsValue(ranking?.ranking_points);
                  const classDelta = formatClassementWeekDelta(ranking);
                  const pointsDelta = formatPointsWeekDelta(ranking?.points_week_delta);
                  const system = ranking?.ranking_system_label || ranking?.ranking_system;
                  const classementLine = [
                    ranking?.classement || null,
                    points ? `${points} ${t("flttPtsShort") || "pts"}` : null,
                  ]
                    .filter(Boolean)
                    .join(" · ");
                  return (
                    <div className="ios-attribute-card ios-attribute-card--ranking">
                      <div className="ios-attribute-icon-wrapper" style={{ color: "#32D74B" }}>
                        📈
                      </div>
                      <div className="ios-attribute-info">
                        <span className="ios-attribute-title">
                          {t("classementAndPoints", "Classement & points")}
                          {system ? ` · ${system}` : ""}
                        </span>
                        <span className="ios-attribute-value ios-ranking-value">
                          {classementLine || "—"}
                        </span>
                        {(classDelta || pointsDelta) && (
                          <span className="ios-ranking-week" aria-label={t("flttWeekChange") || "This week"}>
                            {classDelta ? (
                              <span className={`ios-ranking-chip ios-ranking-chip--${classDelta.tone}`}>
                                {classDelta.text}
                              </span>
                            ) : null}
                            {pointsDelta ? (
                              <span className={`ios-ranking-chip ios-ranking-chip--${pointsDelta.tone}`}>
                                {pointsDelta.text} {t("flttPtsShort") || "pts"}
                              </span>
                            ) : null}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* National ranking place */}
                {(() => {
                  const ranking = getProfileRanking();
                  const place = formatRankingPlace(ranking?.ranking_place);
                  const placeDelta = formatPlaceWeekDelta(ranking?.place_week_delta);
                  return (
                    <div className="ios-attribute-card ios-attribute-card--ranking">
                      <div className="ios-attribute-icon-wrapper" style={{ color: "#0A84FF" }}>
                        🏅
                      </div>
                      <div className="ios-attribute-info">
                        <span className="ios-attribute-title">
                          {t("nationalRanking", "National ranking")}
                          {ranking?.ranking_system === "FLTT"
                            ? ` · ${t("flttVbRglPlace") || "VB-RGL"}`
                            : ""}
                        </span>
                        <span className="ios-attribute-value ios-ranking-value">
                          {place || "—"}
                        </span>
                        {placeDelta ? (
                          <span className="ios-ranking-week" aria-label={t("flttWeekChange") || "This week"}>
                            <span className={`ios-ranking-chip ios-ranking-chip--${placeDelta.tone}`}>
                              {placeDelta.text}
                            </span>
                          </span>
                        ) : null}
                      </div>
                    </div>
                  );
                })()}

                {/* Grip */}
                <div className="ios-attribute-card">
                  <div className="ios-attribute-icon-wrapper" style={{ color: "#30D5C8" }}>
                    ✋
                  </div>
                  <div className="ios-attribute-info">
                    <span className="ios-attribute-title">{t("racketGrip")}</span>
                    <span className="ios-attribute-value">{getPlayerGrip()}</span>
                  </div>
                </div>

                {/* Playing Hand */}
                <div className="ios-attribute-card">
                  <div className="ios-attribute-icon-wrapper" style={{ color: "#BF5AF2" }}>
                    ✍️
                  </div>
                  <div className="ios-attribute-info">
                    <span className="ios-attribute-title">{t("playingHand")}</span>
                    <span className="ios-attribute-value">{getPlayerHand()}</span>
                  </div>
                </div>

                {/* Style of Play (Full Width) */}
                <div className="ios-attribute-card full-width">
                  <div className="ios-attribute-icon-wrapper" style={{ color: "#FF9F0A" }}>
                    ⚡
                  </div>
                  <div className="ios-attribute-info">
                    <span className="ios-attribute-title">{t("playingStyle")}</span>
                    <span className="ios-attribute-value">{getPlayerStyle()}</span>
                  </div>
                </div>
              </div>

              {/* Training Objectives Card */}
              {user.userFocuses && (user.userFocuses.primary || (user.userFocuses.secondary && user.userFocuses.secondary.length > 0)) && (
                <div className="ios-objectives-card">
                  <div className="ios-objectives-header">
                    <span>🎯</span>
                    <h3>{t("trainingGoals")}</h3>
                  </div>
                  
                  {user.userFocuses.primary && (
                    <div className="ios-objective-section">
                      <span className="ios-objective-subtitle primary">
                        🎯 {t("primaryFocus")}
                      </span>
                      <div className="ios-primary-box">
                        {getObjectiveNameByLanguage(user.userFocuses.primary)}
                      </div>
                    </div>
                  )}

                  {user.userFocuses.secondary && user.userFocuses.secondary.length > 0 && (
                    <div className="ios-objective-section">
                      <span className="ios-objective-subtitle secondary">
                        ⚡️ {t("secondaryFocuses")}
                      </span>
                      <div className="ios-secondary-list">
                        {user.userFocuses.secondary
                          .map((s, index) => {
                            const name = getObjectiveNameByLanguage(s);
                            if (!name) return null;
                            return (
                              <div key={index} className="ios-secondary-item">
                                <div className="ios-bullet" />
                                <span className="ios-secondary-text">{name}</span>
                              </div>
                            );
                          })
                          .filter(Boolean)}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <SelfConsentCard userId={user?.user_id} />

              <div className="who-capsule-profile">
                <ScforhWhoCapsule
                  bands={[resolveWhoBandFromBirthDate(user?.user_date_birth || user?.dateBorn)]}
                  variant="compact"
                />
              </div>
            </div>
          )}

          {activeTab === 1 && (
            <div className="ios-tab-content">
              <ProfileSmartTab />
            </div>
          )}

          {activeTab === 2 && (
            <div className="ios-tab-content ios-tab-content--stats">
              <span className="ios-stats-title">{t("activityOverview")}</span>

              {statsLoading ? (
                <div className="ios-activity-card">
                  <span className="ios-activity-sub">{t("loading")}</span>
                </div>
              ) : (
                <>
              {profileStats?.officialRanking ? (
                <FlttRankingSlip
                  ranking={profileStats.officialRanking}
                  history={profileStats.rankingHistory}
                  t={t}
                />
              ) : null}
              {/* Progress Ring Activity Card */}
              <div className="ios-activity-card ios-activity-card--weekly">
                <div className="ios-ring-container" aria-hidden="true">
                  <svg className="ios-ring-svg" viewBox="0 0 80 80" width="80" height="80">
                    <circle cx="40" cy="40" r={ringRadius} className="ios-ring-bg" />
                    <circle
                      cx="40"
                      cy="40"
                      r={ringRadius}
                      className="ios-ring-fill"
                      strokeDasharray={ringCircumference}
                      strokeDashoffset={ringStrokeOffset}
                    />
                  </svg>
                  <span className="ios-ring-text">{Math.round(progressPct * 100)}%</span>
                </div>
                
                <div className="ios-activity-info">
                  <div className="ios-stat-heading">
                    <span className="ios-stat-icon ios-stat-icon--train" aria-hidden="true">
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none">
                        <circle cx="12" cy="12" r="8.25" stroke="currentColor" strokeWidth="1.8" />
                        <path d="M12 8v4.2l2.6 1.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                      </svg>
                    </span>
                    <h4>{t("weeklyTraining")}</h4>
                  </div>
                  <span className="ios-activity-value">
                    {t("weeklyTrainingProgress", { current: weeklyMins, goal: weeklyGoal })}
                  </span>
                  <span className="ios-activity-sub">
                    {t("weeklyGoalAchieved", { percent: Math.round(progressPct * 100) })}
                  </span>
                </div>
              </div>

              {/* Consistency Streak Card */}
              <div className="ios-activity-card ios-activity-card--streak">
                <div className="ios-stat-icon-well ios-stat-icon-well--streak" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="26" height="26" fill="none">
                    <path
                      d="M12 3c1.2 2.4.4 4.1-.6 5.4-1.2 1.5-1.8 2.7-1.4 4.3.3 1.3 1.3 2.3 2.5 2.8"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    />
                    <path
                      d="M12.4 21c3.6-.2 6.1-3.1 5.6-6.5-.3-2.1-1.5-3.5-2.5-4.7-.5 1.5-1.6 2.5-3.1 2.8 1.1-2.5.4-4.4-.8-6.1C8.4 9.2 6 11.6 6 15c0 3.4 2.8 6 6.4 6z"
                      fill="currentColor"
                      opacity="0.92"
                    />
                  </svg>
                </div>
                <div className="ios-streak-info">
                  <h4>{t("activeStreak")}</h4>
                  <span className="ios-streak-title">
                    {activeStreakDays > 0
                      ? t("streakTitle", { count: activeStreakDays })
                      : t("noActiveStreak")}
                  </span>
                  <p className="ios-streak-sub">
                    {activeStreakDays > 0 ? t("streakSubtitle") : t("streakSubtitleEmpty")}
                  </p>
                </div>
              </div>

              {/* Last Session Cardio Metrics */}
              <div className="ios-cardio-card">
                <div className="ios-stat-heading">
                  <span className="ios-stat-icon ios-stat-icon--cardio" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none">
                      <path
                        d="M3.5 12h3.2l2-4.5 3.6 9 2.2-4.5H20.5"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <span className="ios-cardio-header">{t("lastSessionCardio")}</span>
                </div>
                
                <div className="ios-mini-metrics-row">
                  <div className="ios-mini-metric-item ios-mini-metric-item--hr">
                    <span className="ios-metric-glyph" aria-hidden="true">
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
                        <path
                          d="M12 20s-6.5-4.1-8.7-7.6C1.4 9.7 2.7 6.5 5.7 5.6c1.7-.5 3.4.1 4.4 1.4C11.1 5.7 12.8 5.1 14.5 5.6c3 .9 4.3 4.1 2.4 6.8C14.7 15.9 12 20 12 20z"
                          fill="currentColor"
                        />
                      </svg>
                    </span>
                    <span className="ios-metric-value">{formatHr(lastCardio.avgHr)}</span>
                    <span className="ios-metric-label">{t("avgHr")}</span>
                  </div>

                  <div className="ios-mini-metric-item ios-mini-metric-item--max">
                    <span className="ios-metric-glyph" aria-hidden="true">
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
                        <path
                          d="M4 16l4.2-5.2 3.1 3.4L16.5 8 20 12"
                          stroke="currentColor"
                          strokeWidth="1.9"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path d="M4 19h16" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
                      </svg>
                    </span>
                    <span className="ios-metric-value">{formatHr(lastCardio.maxHr)}</span>
                    <span className="ios-metric-label">{t("maxHr")}</span>
                  </div>

                  <div className="ios-mini-metric-item ios-mini-metric-item--kcal">
                    <span className="ios-metric-glyph" aria-hidden="true">
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
                        <path
                          d="M12.2 3.5c.8 1.8.2 3.2-.7 4.3-.9 1.1-1.4 2.1-1.1 3.4.2 1 .9 1.8 1.9 2.2"
                          stroke="currentColor"
                          strokeWidth="1.7"
                          strokeLinecap="round"
                        />
                        <path
                          d="M12.5 20.5c2.9-.2 5-2.5 4.6-5.2-.3-1.7-1.3-2.9-2.1-3.9-.4 1.2-1.3 2-2.5 2.2.9-2 .3-3.5-.7-4.9-2.5 2.1-4.4 4.1-4.4 6.8 0 2.8 2.3 5 5.1 5z"
                          fill="currentColor"
                        />
                      </svg>
                    </span>
                    <span className="ios-metric-value">{formatCalories(lastCardio.calories)}</span>
                    <span className="ios-metric-label">{t("calories")}</span>
                  </div>
                </div>
              </div>
                </>
              )}
            </div>
          )}

          {activeTab === 3 && (
            <div className="ios-tab-content">
              <span className="ios-stats-title">{t("customGearSlots")}</span>
              
              {showFeedback && (
                <div className="ios-local-toast">
                  <span>✨</span> {t("gearEquippedSuccess")}
                </div>
              )}

              {/* Blade Slot */}
              {editingSlot === "blade" ? (
                <div className="ios-gear-slot blade">
                  <div className="ios-gear-edit-form">
                    <span className="ios-gear-edit-title" style={{ color: "var(--brand-warning)" }}>
                      {t("editCoreBlade")}
                    </span>
                    <div className="ios-gear-edit-inputs">
                      <input
                        type="text"
                        className="ios-gear-input"
                        value={tempGearName}
                        onChange={(e) => setTempGearName(e.target.value)}
                        autoFocus
                      />
                      <button className="ios-gear-btn-save" disabled={gearSaving} onClick={() => saveEditingGear("blade")}>
                        {t("save")}
                      </button>
                      <button className="ios-gear-btn-cancel" disabled={gearSaving} onClick={cancelEditingGear}>
                        {t("cancel")}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="ios-gear-slot blade">
                  <div className="ios-gear-left">
                    <div className="ios-gear-icon-wrapper">🏓</div>
                    <div className="ios-gear-text">
                      <span className="ios-gear-slot-title">{t("coreBlade")}</span>
                      <span className="ios-gear-name">{displayGearValue(blade)}</span>
                    </div>
                  </div>
                  <button className="ios-gear-edit-btn" onClick={() => startEditingGear("blade", blade)}>
                    ✏️
                  </button>
                </div>
              )}

              {/* Forehand Slot */}
              {editingSlot === "forehand" ? (
                <div className="ios-gear-slot forehand">
                  <div className="ios-gear-edit-form">
                    <span className="ios-gear-edit-title" style={{ color: "var(--brand-danger)" }}>
                      {t("editForehandRubber")}
                    </span>
                    <div className="ios-gear-edit-inputs">
                      <input
                        type="text"
                        className="ios-gear-input"
                        value={tempGearName}
                        onChange={(e) => setTempGearName(e.target.value)}
                        autoFocus
                      />
                      <button className="ios-gear-btn-save" disabled={gearSaving} onClick={() => saveEditingGear("forehand")}>
                        {t("save")}
                      </button>
                      <button className="ios-gear-btn-cancel" disabled={gearSaving} onClick={cancelEditingGear}>
                        {t("cancel")}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="ios-gear-slot forehand">
                  <div className="ios-gear-left">
                    <div className="ios-gear-icon-wrapper">🔴</div>
                    <div className="ios-gear-text">
                      <span className="ios-gear-slot-title">{t("forehandRubber")}</span>
                      <span className="ios-gear-name">{displayGearValue(forehand)}</span>
                    </div>
                  </div>
                  <button className="ios-gear-edit-btn" onClick={() => startEditingGear("forehand", forehand)}>
                    ✏️
                  </button>
                </div>
              )}

              {/* Backhand Slot */}
              {editingSlot === "backhand" ? (
                <div className="ios-gear-slot backhand">
                  <div className="ios-gear-edit-form">
                    <span className="ios-gear-edit-title" style={{ color: "var(--brand-primary)" }}>
                      {t("editBackhandRubber")}
                    </span>
                    <div className="ios-gear-edit-inputs">
                      <input
                        type="text"
                        className="ios-gear-input"
                        value={tempGearName}
                        onChange={(e) => setTempGearName(e.target.value)}
                        autoFocus
                      />
                      <button className="ios-gear-btn-save" disabled={gearSaving} onClick={() => saveEditingGear("backhand")}>
                        {t("save")}
                      </button>
                      <button className="ios-gear-btn-cancel" disabled={gearSaving} onClick={cancelEditingGear}>
                        {t("cancel")}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="ios-gear-slot backhand">
                  <div className="ios-gear-left">
                    <div className="ios-gear-icon-wrapper">⚫</div>
                    <div className="ios-gear-text">
                      <span className="ios-gear-slot-title">{t("backhandRubber")}</span>
                      <span className="ios-gear-name">{displayGearValue(backhand)}</span>
                    </div>
                  </div>
                  <button className="ios-gear-edit-btn" onClick={() => startEditingGear("backhand", backhand)}>
                    ✏️
                  </button>
                </div>
              )}

              {/* Weight Metric Card */}
              <div className="ios-weight-card">
                <div className="ios-weight-left">
                  <span className="ios-weight-icon">⚖️</span>
                  <span>{t("setupWeight")}</span>
                </div>
                <span className="ios-weight-value">
                  {gearWeight ? gearWeight : t("notSet")}
                </span>
              </div>
            </div>
          )}
        </div>
        </>
        ) : null}

        {(isCoachView || isAdminStaff) ? (
          <p className="profile-staff-health-note">
            {t("digipingClubScforhBulletADesc")}
          </p>
        ) : null}
      </div>
    </div>
  );
};
